import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./KYC-PrimaryContact.css";
import { Breadcrumb, Button, Card, Spin } from "antd";
import { DoubleLeftOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import {
	KYCAccountNameImage,
	KYCContactsImage,
	KYCCylindersImage,
	KYCDesignImage,
	KYCMaterialsImage,
} from "../Elite/imageAssets";

const defaultPayload = {
	fromDate: "2026-04-09T12:10:21.201Z",
	toDate: "2026-04-09T12:10:21.201Z",
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const getValue = (obj, keys) => {
	for (const key of keys) {
		const value = obj?.[key];
		if (
			value !== undefined &&
			value !== null &&
			value !== "" &&
			value !== "Microsoft.Xrm.Sdk.OptionSetValue"
		) {
			return value;
		}
	}

	return "-";
};

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const KycPrimaryContactDetails = () => {
	const { sapCode } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [isCardsLoading, setIsCardsLoading] = useState(false);
	const [primaryContactData, setPrimaryContactData] = useState({});
	const [accountNameCount, setAccountNameCount] = useState({});
	const [contactsCount, setContactsCount] = useState({});
	const [materialsCount, setMaterialsCount] = useState({});
	const [designsCount, setDesignsCount] = useState({});
	const [cylindersCount, setCylindersCount] = useState({});

	const fromAccountTab = Boolean(location.state?.fromAccountTab);
	const groupCompanyBreadcrumbLabel = location.state?.groupCompanyName || "Group Company";
	const accountBreadcrumbLabel = location.state?.accountName || "Account";

	const getCardTitle = (countObj, label, keys = ["total"]) => {
		const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
		return countValue !== undefined ? `${countValue} ${label}` : label;
	};

	useEffect(() => {
		if (!sapCode) {
			return;
		}

		setIsLoading(true);
		KycService.getPrimaryContactDataBySapCodeAPI(sapCode)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data?.responseData;
					const finalData = Array.isArray(responseData) ? responseData[0] || {} : responseData || {};
					setPrimaryContactData(finalData);
				} else {
					NotificationManager.error(response.data?.message || "Unable to load primary contact details.");
					setPrimaryContactData({});
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load primary contact details.");
				setPrimaryContactData({});
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [sapCode]);

	useEffect(() => {
		setIsCardsLoading(true);
		KycService.getKycTileCountsAPI(defaultPayload)
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};
					setAccountNameCount(apiData.company ? apiData.company : {});
					setContactsCount(apiData.contacts ? apiData.contacts : {});
					setMaterialsCount(apiData.materials ? apiData.materials : {});
					setDesignsCount(apiData.designs ? apiData.designs : {});
					setCylindersCount(apiData.cylinders ? apiData.cylinders : {});
				} else {
					NotificationManager.error(response.data?.message || "Unable to load KYC tile counts.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
			})
			.finally(() => {
				setIsCardsLoading(false);
			});
	}, []);

	const detailItems = useMemo(
		() => [
			{ label: "Title", value: getValue(primaryContactData, ["title", "jobTitle", "designation", "ispl_Title"]) },
			{ label: "First Name", value: getValue(primaryContactData, ["firstName", "firstname", "ispl_FirstName"]) },
			{ label: "Email", value: getValue(primaryContactData, ["email", "emailaddress1", "primaryEmail"]) },
			{
				label: "Mobile Phone",
				value: getValue(primaryContactData, ["mobilePhone", "mobilephone", "telephone1", "phone"]),
			},
			{
				label: "Address 1",
				value: getValue(primaryContactData, ["address1_Line1", "address1_line1", "address1"]),
			},
			{
				label: "Address 2",
				value: getValue(primaryContactData, ["address1_Line2", "address1_line2", "address2"]),
			},
			{ label: "Area", value: getValue(primaryContactData, ["address1_Line3", "address1_county", "area", "areaName"]) },
			{
				label: "Postal Code",
				value: getValue(primaryContactData, ["ispl_PostCode", "address1_postalcode", "postalCode", "zipcode"]),
			},
			{ label: "City", value: getValue(primaryContactData, ["ispl_City", "address1_city", "city"]) },
			{ label: "District", value: getValue(primaryContactData, ["ispl_District", "district", "address1_district"]) },
			{ label: "State", value: getValue(primaryContactData, ["ispl_State", "address1_stateorprovince", "state", "stateName"]) },
			{ label: "Country", value: getValue(primaryContactData, ["ispl_Country", "address1_country", "country", "countryName"]) },
		],
		[primaryContactData],
	);

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								<Breadcrumb.Item>
									<Link to="/kyc?companyTab=groupCompany">
										<label>Group Company</label>
										{/* <label>{groupCompanyBreadcrumbLabel}</label> */}
									</Link>
								</Breadcrumb.Item>
								{fromAccountTab ? (
									<Breadcrumb.Item>
										<Link to="/kyc?companyTab=account">
											<label>Account</label>
										</Link>
										{/* <label>{accountBreadcrumbLabel}</label> */}
									</Breadcrumb.Item>
								) : null}
								<Breadcrumb.Item>
									<label>Primary Contact</label>
								</Breadcrumb.Item>
							</Breadcrumb>
							<Link to="/kyc">
								<Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
						</div>
						<Spin size="large" spinning={isLoading || isCardsLoading}>
							<div className="row">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_yellow">
										<div className="card-content">
											<div className="kyc-card-body KYCAccountName images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCAccountNameImage} alt="Img" />
														<span>
															{getCardTitle(accountNameCount, "Account Name", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															-
														</h5>
														<h6 className="text-white mb-0">Ship to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															-
														</h5>
														<h6 className="text-white mb-0">Bill to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															-
														</h5>
														<h6 className="text-white mb-0">Payer</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_red">
										<div className="card-content">
											<div className="kyc-card-body KYCContacts images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCContactsImage} alt="Img" />
														<span>
															{getCardTitle(contactsCount, "Contacts", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(contactsCount).length > 0
																? contactsCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(contactsCount).length > 0
																? contactsCount.hide
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_blue">
										<div className="card-content">
											<div className="kyc-card-body KYCMaterials images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCMaterialsImage} alt="Img" />
														<span>
															{getCardTitle(materialsCount, "Materials", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(materialsCount).length > 0
																? materialsCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(materialsCount).length > 0
																? materialsCount.hide
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_grey">
										<div className="card-content">
											<div className="kyc-card-body KYCDesign images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCDesignImage} alt="Img" />
														<span>
															{getCardTitle(designsCount, "Designs", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(designsCount).length > 0
																? designsCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(designsCount).length > 0
																? designsCount.hide
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple">
										<div className="card-content">
											<div className="kyc-card-body KYCCylinders images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCCylindersImage} alt="Img" />
														<span>
															{getCardTitle(cylindersCount, "Cylinders", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(cylindersCount).length > 0
																? cylindersCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(cylindersCount).length > 0
																? cylindersCount.close
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Close</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

							</div>

							<Card className="kyc-primary-details-card">
								<div className="kyc-primary-details-shell">
									<h3 className="kyc-primary-details-title">Primary Contact Details</h3>
									<div className="row">
										{detailItems.map((item) => (
											<div className="col-xl-3 col-lg-4 col-md-6 col-12" key={item.label}>
												<div className="kyc-primary-detail-item">
													<label>{item.label}:</label>
													<p>{item.value}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycPrimaryContactDetails;
